import {
  ComponentType,
  DashboardState,
  DragDropInsertMethod,
  EElementDragReason,
  EProjectType,
  ICustomBlockUsedStyles,
  IDraggableComponent,
  IProject,
  SpecialDIIds,
  ViewPart
} from "@kuika/kuika-cl-model";
import { Fzf } from "fzf";
import _ from "lodash";
import { Panel } from "rc-collapse";
import { PureComponent } from "react";
import ReactDOM from "react-dom";
import { IScreenDesignContextState } from "../../../../../contexts/screen-design-context/screen-design-context";
import { BlockModel, BlocksService } from "../../../../../services/blocks-service";
import { WidgetComponentInfo, WidgetProjectInfo, WidgetsService } from "../../../../../services/widgets-service";
import {
  filterPlaceableComponents,
  type IPlaceableComponentsContext,
  isDropTargetComponentType,
  isReactNativeOnlyComponent
} from "../../../../../shared/utility/component-palette";
import {
  resolveLoadingPresentation,
  shouldShowCatalogueSkeleton
} from "../../../../../shared/utility/first-load-skeletons";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { DeleteModal } from "../../../../common/delete-modal";
import { KModal } from "../../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../../ui-kit/k-modal/k-modal-footer";
import KuikaBadge from "../../../../ui-kit/kuika-badge/kuika-badge";
import KuikaCollapsePanel from "../../../../ui-kit/kuika-collapse-panel/kuika-collapse-panel";
import KuikaComponentIcon from "../../../../ui-kit/kuika-component-icon/kuika-component-icon";
import KuikaEmptyState from "../../../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaModalIconWrapper from "../../../../ui-kit/kuika-modal-icon-wrapper/kuika-modal-icon-wrapper";
import KuikaOverflowText from "../../../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../../../../ui-kit/kuika-overflow-text/overflow-reveal-controller";
import {
  KuikaSkeletonCardGrid,
  KuikaSkeletonList,
  KuikaSkeletonRegion
} from "../../../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSmallSpinner from "../../../../ui-kit/kuika-small-spinner/kuika-small-spinner";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabContent from "../../../../ui-kit/kuika-tab-content/kuika-tab-content";
import KuikaTabs from "../../../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaTooltip from "../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaH5 from "../../../../ui-kit/typography/H5/H5";
import KuikaSmallText from "../../../../ui-kit/typography/SmallText/SmallText";
import KuikaText from "../../../../ui-kit/typography/Text/Text";
import { isReactNativeGenerationOffered } from "../../../app-settings/react-native-feature-gate";
import SearchablePanelHeader from "../../searchable-panel-header/searchable-panel-header";
import BlockItem from "./block-item";
import ComponentToolKitSkeleton from "./component-toolkit-skeleton";
import "./component-toolkit.scss";
import { searchKeywords } from "./searchKeywords";

interface ISearchKeywords {
  name: string;
  keywords: string[];
}

interface IComponentToolKitState {
  isSearchActive: boolean;
  searchKeyWords: ISearchKeywords[];
  searchText: string;
  categoryList: IDraggableComponent[];
  customCategoryName: string;
  previewDropdownVisible: boolean;
  activeKeys: string[];
  foundedKeywords: string[];
  mode: string;
  widgetProjects: WidgetProjectInfo[];
  isWidgetsLoading: boolean;
  deletingWidgetPackId: string | null;
  deleteConfirmProject: WidgetProjectInfo | null;
  itemToDelete?: { id: string; name: string };
  isDeleteModalVisible?: boolean;
}

interface IComponentToolKitProps {
  project: IProject;
  sd: IScreenDesignContextState;
  designType: string;
}

/** What renderPanelContent needs from render()'s already-computed locals. */
interface IPanelContentArgs {
  showCatalogueSkeleton: boolean;
  mode: string;
  searchText: string;
  activeKeys: string[];
  hasSearchResults: boolean;
  commonlyUsedComponents: JSX.Element;
}

/** One of the panel's mode tabs (Elements / Blocks / Widgets). */
interface IComponentPanelTabOption {
  label: string;
  value: string;
  dataTestId: string;
}

const commonlyUsedComponentNames = [
  "Row",
  "Header",
  "HorizontalStack",
  "VerticalStack",
  "Label",
  "Button",
  "TextInput",
  "Icon",
  "Table"
];

// BlockItem moved to its own file

class ComponentToolKit extends PureComponent<IComponentToolKitProps, IComponentToolKitState> {
  private componentsFzf?: Fzf<IDraggableComponent[]>;

  private componentsFzfKey?: string;

  private blocksFzf?: Fzf<BlockModel[]>;

  private blocksFzfKey?: string;

  constructor(props: IComponentToolKitProps) {
    super(props);
    this.state = {
      isSearchActive: false,
      searchKeyWords: searchKeywords,
      searchText: "",
      categoryList: [],
      customCategoryName: "",
      previewDropdownVisible: false,
      activeKeys: ["0", "1", "2", "99"],
      foundedKeywords: [],
      mode: "elements",
      widgetProjects: [],
      isWidgetsLoading: false,
      deletingWidgetPackId: null,
      deleteConfirmProject: null
    };
  }

  componentDidMount() {
    this.initSearchText();
    this.ensureHtmlBufferExists();
  }

  componentWillUnmount() {
    // Clear screenshot cache to prevent memory leaks
    BlocksService.clearScreenshotCache();
  }

  initSearchText = () => {
    const searchText = this.getSearchTextFromProps();
    if (searchText && searchText.length > 0) {
      this.setState({ isSearchActive: true, searchText });
    }
  };

  getSearchTextFromProps = () => {
    const { sd } = this.props;
    if (sd.dashboardState === DashboardState.reportDesigner) {
      return sd.searchTexts[ViewPart.ReportDesignerPropertiesPanel];
    }
    return sd.searchTexts[ViewPart.ComponentsPanel];
  };

  resetActiveKeys = () => {
    this.setState({ activeKeys: ["0", "1", "2", "99"] });
  };

  handleComponentClick = (name: string, id: string) => {
    const { sd } = this.props;
    if (sd.dashboardState === DashboardState.reportDesigner) return;

    const kuikaid = this.generateRandomId().toString();
    const di = this.getSelectedDisplayItem();

    if (di && di.item) {
      const targetKuikaId = this.getTargetKuikaId(di);
      if (targetKuikaId && name !== "Form") {
        sd.dragDropByCreateItemMessage({
          insertMethod: DragDropInsertMethod.insertInto,
          sourceKuikaId: kuikaid,
          sourceName: name,
          sourceId: id,
          targetKuikaId
        });
      }
    }
  };

  generateRandomId = () => Math.floor(Math.random() * 1000000) + 1;

  getSelectedDisplayItem = () => this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());

  getTargetKuikaId = (di: any) => {
    const sd = this.props.sd.getScreenDesign();
    const diItem = di.item;
    const compDef = this.props.sd.getComponentDefinition(diItem);
    if (this.isDroppableContainer(compDef?.componentType)) {
      return di.id.toString();
    }
    const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());
    const selectedDisParentDi = selectedDi?.parentId
      ? this.props.sd.screenDesignDictionary[selectedDi.parentId]
      : undefined;
    const parentDi = selectedDisParentDi;
    const parentCompDef = this.props.sd.getComponentDefinition(parentDi?.item);
    if (parentCompDef && this.isDroppableContainer(parentCompDef.componentType) && parentDi) {
      return parentDi.id.toString();
    }
    return null;
  };

  // Shared with the workflow screen builder, which asks the same question before placing a
  // component (KOA-18431). Same set, one definition.
  isDroppableContainer = (componentType?: ComponentType) => isDropTargetComponentType(componentType);

  deleteSearchText = () => {
    this.setState({ foundedKeywords: [] });
    this.resetActiveKeys();
  };

  getProjectComponents = (): IDraggableComponent[] => {
    const result: IDraggableComponent[] = [];
    const { project, designType, sd } = this.props;
    const isWebScreen = sd.getIsWebScreen();
    // RN_BETA_ALLOWLIST: a user who cannot generate React Native has no use for a component only React
    // Native renders, so WebAndReactNative is hidden from them on mobile screens instead of badged.
    const reactNativeOffered: boolean = isReactNativeGenerationOffered();

    if (!project?.componentSetObj?.components) {
      return result;
    }

    // The placeability rule lives in `component-palette.ts`: the AI's catalog tool and the workflow
    // screen builder's palette ask the same question, and the copies had to stop multiplying
    // (KOA-18431). Behaviour here is unchanged — this panel's own filter WAS that rule.
    const paletteContext: IPlaceableComponentsContext = {
      projectType: project.projectType,
      isWebScreen,
      isReportMode: designType === "report",
      reactNativeOffered
    };
    filterPlaceableComponents(project.componentSetObj.components, paletteContext).forEach((component) => {
      const { id, name, blockStyle, componentType, icon, componentCategoryName, componentCategory } = component;
      result.push({
        id,
        name,
        blockStyle,
        componentType,
        icon,
        categoryName: componentCategoryName ?? componentCategory,
        componentCategory,
        isReactNativeOnly: isReactNativeOnlyComponent(component, paletteContext)
      });
    });

    return result.sort((a, b) => a.name.localeCompare(b.name));
  };

  onPanelChange = (key: any) => {
    this.setState({ activeKeys: key });
  };

  handleDragEnter = (ev: any) => {
    ev.preventDefault();
    ev.stopPropagation();
    if (ev.currentTarget) {
      const activeCanvasIFrame = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
      if (activeCanvasIFrame) {
        (activeCanvasIFrame.contentWindow as any).kuika.activeDropTargetItem = ev.currentTarget;
      }
    }
  };

  getComponentIconImgByIconName = (iconName: string, componentName: string, categoryName: string) => {
    const sanitizedName = this.handleCharsAndDeleteWhiteSpaces(componentName)?.toLowerCase() || "";
    const sanitizedCategoryName = this.handleCharsAndDeleteWhiteSpaces(categoryName)?.toLowerCase() || "";
    const iconId = `component-icon-${sanitizedCategoryName}-${sanitizedName}`;
    return <KuikaComponentIcon id={iconId} name={iconName} width={48} height={48} />;
  };

  convertPascalCaseToKebabCase = (str?: string) => {
    if (str) {
      if (str === "QRRenderer") return "qr-renderer";
      if (str === "MDEditor") return "md-editor";
      if (str === "MDViewer") return "md-viewer";
      str = str.replace(/\s+/g, "");
      return str.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
    }
  };

  handleCharsAndDeleteWhiteSpaces = (text: string) => {
    if (!text) return "";
    return _.deburr(text).replace(/\s/g, "");
  };

  getKuikaDraggableComponent = (
    name: string,
    id: string,
    componentType: any,
    blockStyle: any,
    icon?: any,
    categoryName?: string,
    isReactNativeOnly: boolean = false
  ) => {
    const handleDragStart = (event: any) => {
      const activeCanvasIFrame = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
      if (activeCanvasIFrame) {
        (activeCanvasIFrame.contentWindow as any).kuika.movingSourceElement = {
          id,
          name,
          componentType,
          blockStyle
        } as IDraggableComponent;
      }
      this.props.sd.setSelectedDisplayItem(SpecialDIIds.rootDI.toString());
      event.dataTransfer.setData("name", event.target.id || event.currentTarget.id);
      event.dataTransfer.setData("id", id.toString());
      event.dataTransfer.setData("kuikaid", this.generateRandomId().toString());
      event.dataTransfer.setData("reason", EElementDragReason.CREATE);
      event.dataTransfer.setData(`kuikaComponentType=${componentType}`, componentType);

      const dragImage = document.createElement("div");
      dragImage.style.position = "absolute";
      dragImage.style.top = "3px";
      dragImage.style.left = "15px";
      dragImage.style.zIndex = "-1";

      const badgeContainer = document.createElement("div");
      badgeContainer.className = "kuika-component-panel__drag-preview";

      const badge = (
        <div className="kuika-component-panel__drag-preview">
          <KuikaBadge
            className="kuika-padding-right-l"
            content={name?.toUpperCase()}
            leftIcon={<KuikaSvg name={"plus"} width={12} height={12} />}
            color="orange"
          />
          <div className="kuika-component-panel__drag-preview--copy-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g id="copy-plus" clipPath="url(#clip0_10513_34988)">
                <path
                  id="Vector"
                  d="M7 9.667C7 8.95967 7.28099 8.28131 7.78115 7.78115C8.28131 7.28099 8.95967 7 9.667 7H18.333C18.6832 7 19.03 7.06898 19.3536 7.20301C19.6772 7.33704 19.9712 7.53349 20.2189 7.78115C20.4665 8.0288 20.663 8.32281 20.797 8.64638C20.931 8.96996 21 9.31676 21 9.667V18.333C21 18.6832 20.931 19.03 20.797 19.3536C20.663 19.6772 20.4665 19.9712 20.2189 20.2189C19.9712 20.4665 19.6772 20.663 19.3536 20.797C19.03 20.931 18.6832 21 18.333 21H9.667C9.31676 21 8.96996 20.931 8.64638 20.797C8.32281 20.663 8.0288 20.4665 7.78115 20.2189C7.53349 19.9712 7.33704 19.6772 7.20301 19.3536C7.06898 19.03 7 18.6832 7 18.333V9.667Z"
                  stroke="black"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  id="Vector_2"
                  d="M4.012 16.737C3.705 16.5626 3.44965 16.31 3.2719 16.0049C3.09415 15.6998 3.00034 15.3531 3 15V5C3 3.9 3.9 3 5 3H15C15.75 3 16.158 3.385 16.5 4"
                  stroke="black"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  id="Vector_3"
                  d="M11 14H17"
                  stroke="black"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  id="Vector_4"
                  d="M14 11V17"
                  stroke="black"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
              <defs>
                <clipPath id="clip0_10513_34988">
                  <rect width="24" height="24" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </div>
        </div>
      );

      const buffer = document.getElementById("html-buffer");
      if (buffer) {
        ReactDOM.render(badge, dragImage);
        buffer.appendChild(dragImage);

        event.dataTransfer.setDragImage(dragImage, dragImage.clientWidth / 2, dragImage.clientHeight / 2);

        setTimeout(() => {
          try {
            buffer.removeChild(dragImage);
          } catch (e) {}
        }, 100);
      } else {
        const fallbackDragImage = document.createElement("div");
        fallbackDragImage.style.cssText = `
          background-color: rgb(104, 185, 102);
          color: var(--white);
          font-weight: 900;
          position: absolute;
          top: 32px;
          left: 15px;
          z-index: -1;
          padding: 5px 10px;
          border-radius: 4px;
        `;
        // Security: use textContent instead of innerHTML to prevent DOM XSS
        fallbackDragImage.textContent = name;
        document.getElementById(`btn_${name}`)?.appendChild(fallbackDragImage);
        event.dataTransfer.setDragImage(fallbackDragImage, 0, 0);
        setTimeout(() => fallbackDragImage.parentNode?.removeChild(fallbackDragImage), 500);
      }
    };

    const isMainAI = KMainFunctions.isMainAI();
    const isBetaComponent = name === "DynamicForm" || name === "DynamicInput";

    const componentElement = (
      <div
        className={`kuika-component-panel__element ${OVERFLOW_REVEAL_HOST_CLASS} ${
          categoryName ? `kuika-component-panel__element--${categoryName}-${name.replace(/\s/g, "").toLowerCase()}` : ""
        }`}
        data-testid={`${name.replace(/\s/g, "").toLowerCase()}Component`}
        data-onboarding-id={`kuika-component-panel_${categoryName
          ?.replace(/\s/g, "")
          .toLowerCase()}_${name.replace(/\s/g, "").toLowerCase()}`}
        key={id}
        draggable={true}
        id={`btn_${name}`}
        data-id={id}
        onDragEnter={this.handleDragEnter}
        onDragStart={handleDragStart}
        onClick={() => this.handleComponentClick(name, id)}
      >
        <div className="kuika-component-panel__element--icon">
          {this.getComponentIconImgByIconName(icon, name, categoryName || "")}
          {isBetaComponent && (
            <div className="component-beta-badge">
              <span className="beta-content">
                β<span className="beta-text">eta</span>
              </span>
            </div>
          )}
          {isReactNativeOnly && (
            <div className="component-beta-badge component-beta-badge--react-native" data-testid="reactNativeOnlyBadge">
              <span className="beta-content">
                RN<span className="beta-text"> only</span>
              </span>
            </div>
          )}
        </div>

        <KuikaSmallText title={name}>
          <KuikaOverflowText>{name}</KuikaOverflowText>
        </KuikaSmallText>
      </div>
    );

    // A clipped name reveals itself on tile hover; only the beta and RN-only markers still need a tooltip.
    return isBetaComponent || isReactNativeOnly ? (
      <KuikaTooltip
        position="bottom"
        content={
          <div>
            {name} {isBetaComponent && <KuikaBadge content="BETA" color="purple" />}
            {isReactNativeOnly && (
              <div className="kuika-component-panel__element--rn-only-note">
                Renders on mobile only with the React Native generation type. A native Android/iOS configuration will
                refuse this component at publish.
              </div>
            )}
          </div>
        }
        delayDuration={250}
        sideOffset={-30}
      >
        {componentElement}
      </KuikaTooltip>
    ) : (
      componentElement
    );
  };

  getFilteredComponents = (category) => {
    const { searchText } = this.state;
    const normalized = this.handleCharsAndDeleteWhiteSpaces(searchText)?.toLowerCase();
    const components = this.getProjectComponents();
    this.ensureComponentsFzf(components);
    if (!normalized || normalized.length === 0) return components;
    return this.componentsFzf ? this.componentsFzf.find(normalized).map((r) => r.item) : components;
  };

  renderCategoryPanel = (category, index) => {
    const { searchText, foundedKeywords } = this.state;
    const normalizedSearchText = this.handleCharsAndDeleteWhiteSpaces(searchText)?.toLowerCase();
    const filteredComponents = this.getFilteredComponents(category)
      .filter(({ name }) => {
        const isKeywordFound = foundedKeywords.some((keyword) => keyword.toLowerCase() === name.toLowerCase());

        if (isKeywordFound) return true;
        if (!searchText || searchText === "" || searchText.length <= 1) return true;
        return name.toLowerCase().includes(normalizedSearchText);
      })
      .filter(({ categoryName }) => categoryName === category.categoryName)
      .sort((a, b) => a.name.localeCompare(b.name));

    if (filteredComponents.length === 0) {
      return null;
    }

    return (
      <Panel
        header={
          <div
            id={`components-panel-header-${this.handleCharsAndDeleteWhiteSpaces(category.categoryName)?.toLowerCase()}`}
            data-onboarding-id={`component-toolkit_${this.handleCharsAndDeleteWhiteSpaces(
              category.categoryName
            )?.toLowerCase()}`}
            className={`components-panel-header-${this.handleCharsAndDeleteWhiteSpaces(
              category.categoryName
            )?.toLowerCase()}`}
            data-testid={category.categoryName.toLowerCase().replace(/\s/g, "") + "ComponentGroupDropdown"}
          >
            {category.categoryName}
          </div>
        }
        key={index}
      >
        <div className="kuika-component-panel__elements-wrapper">
          {filteredComponents.map(({ id, name, componentType, blockStyle, icon, isReactNativeOnly }) =>
            this.getKuikaDraggableComponent(
              name,
              id,
              componentType,
              blockStyle,
              icon,
              category.categoryName,
              isReactNativeOnly === true
            )
          )}
        </div>
      </Panel>
    );
  };

  getFilteredCategories = () => {
    const { project, designType } = this.props;
    const { searchText } = this.state;
    const normalizedSearchText = this.handleCharsAndDeleteWhiteSpaces(searchText)?.toLowerCase();
    const reportDesignerCategories = ["Commonly Used", "Container", "Display", "Data"];
    const componentCategoriesOrder = [
      "Container",
      "Display",
      "Navigation",
      "Text Input",
      "Numeric Input",
      "Select Input",
      "Date and Time Input",
      "Data",
      "Interactive",
      "Chart",
      "Special",
      "Document Management",
      "KuikAgents Integration"
    ];

    const categoryList = project?.componentSetObj?.categoryList || [];

    return categoryList
      .filter((category) => {
        if (designType === "report") {
          return reportDesignerCategories.includes(category.categoryName);
        }

        if (!searchText) return true;

        const hasMatchingComponent = this.getFilteredComponents(category).some(({ name, categoryName }) => {
          const normalizedComponentName = name.toLowerCase();
          const commonCategory = "Commonly Used";
          if (categoryName === commonCategory) {
            if (commonlyUsedComponentNames.includes(name) && normalizedSearchText.length <= 1) return true;
            return false;
          }

          return (
            (normalizedSearchText.length <= 1 && normalizedComponentName.startsWith(normalizedSearchText)) ||
            categoryName === category.categoryName
          );
        });

        return hasMatchingComponent;
      })
      .sort((a, b) => {
        return componentCategoriesOrder.indexOf(a.categoryName) - componentCategoriesOrder.indexOf(b.categoryName);
      });
  };

  getCommonlyUsedComponents = () => {
    const { searchText } = this.state;
    const normalizedSearchText = this.handleCharsAndDeleteWhiteSpaces(searchText)?.toLowerCase();

    const commonlyUsedList = this.getProjectComponents()
      .filter(({ name }) => commonlyUsedComponentNames.includes(name))
      .sort((a, b) => commonlyUsedComponentNames.indexOf(a.name) - commonlyUsedComponentNames.indexOf(b.name))
      .filter(({ name }) => {
        if (!searchText) return true;

        return (
          name.toLowerCase().includes(normalizedSearchText) ||
          "Commonly Used".toLowerCase().includes(normalizedSearchText)
        );
      });

    return (
      <Panel
        header={
          <div id="components-panel-header-commonly-used" data-testid="commonlyusedComponentGroupDropdown">
            Commonly Used
          </div>
        }
        key="99"
      >
        <div className="kuika-component-panel__elements-wrapper" data-testid="commonlyUsedComponentList">
          {commonlyUsedList.map(({ id, name, componentType, blockStyle, icon, isReactNativeOnly }) =>
            this.getKuikaDraggableComponent(
              name,
              id,
              componentType,
              blockStyle,
              icon,
              "commonly-used",
              isReactNativeOnly === true
            )
          )}
        </div>
      </Panel>
    );
  };

  onSearchTextChange = (searchText: string) => {
    this.setState({ searchText });
    if (searchText.length === 0) {
      this.resetActiveKeys();
    }
  };

  setSearchActive = (isSearchActive: boolean) => {
    this.setState({ isSearchActive });
  };

  onModeChange = (mode?: string) => {
    this.setState({ mode: mode || "elements" });
    if (mode === "blocks" && this.props.sd.blocks.length === 0) {
      this.loadBlocks();
    }
    if (mode === "widgets" && this.state.widgetProjects.length === 0) {
      this.loadWidgets();
    }
  };

  loadBlocks = async () => {
    try {
      await this.props.sd.loadBlocks();
      const blocks = (this.props.sd.blocks || []) as BlockModel[];
      this.ensureBlocksFzf(blocks);
    } catch (error) {
      console.error("Failed to load blocks:", error);
    }
  };

  loadWidgets = async () => {
    this.setState({ isWidgetsLoading: true });
    try {
      const response = await WidgetsService.GetWidgetProjects();
      const widgetProjects = (response.data || []) as WidgetProjectInfo[];
      this.setState({ widgetProjects });
    } catch (error) {
      console.error("Failed to load widgets:", error);
      this.setState({ widgetProjects: [] });
    } finally {
      this.setState({ isWidgetsLoading: false });
    }
  };

  private ensureBlocksFzf = (blocks: BlockModel[]) => {
    const key = `${blocks.length}:${blocks[0]?.id || ""}:${blocks[blocks.length - 1]?.id || ""}`;
    if (this.blocksFzf && this.blocksFzfKey === key) return;
    this.blocksFzf = new Fzf(blocks as unknown as BlockModel[], {
      selector: (b: any) => `${b.name || ""} ${b.category || ""}`,
      casing: "case-insensitive"
    });
    this.blocksFzfKey = key;
  };

  private ensureComponentsFzf = (components: IDraggableComponent[]) => {
    const key = `${components.length}:${components[0]?.id || ""}:${components[components.length - 1]?.id || ""}`;
    if (this.componentsFzf && this.componentsFzfKey === key) return;
    this.componentsFzf = new Fzf(components as unknown as IDraggableComponent[], {
      selector: (c: any) => `${c.name} ${c.categoryName || ""}`,
      casing: "case-insensitive"
    });
    this.componentsFzfKey = key;
  };

  ensureHtmlBufferExists = () => {
    if (!document.getElementById("html-buffer")) {
      const buffer = document.createElement("div");
      buffer.id = "html-buffer";
      buffer.style.position = "absolute";
      buffer.style.top = "0";
      buffer.style.left = "0";
      buffer.style.pointerEvents = "none";
      buffer.style.zIndex = "-9999";
      buffer.style.visibility = "hidden";
      document.body.appendChild(buffer);
    }
  };

  renderBlockCategoryPanel = (categoryName: string, blocks: BlockModel[], index: number) => {
    return (
      <Panel
        header={
          <div id={`blocks-panel-header-${this.handleCharsAndDeleteWhiteSpaces(categoryName)?.toLowerCase()}`}>
            {categoryName}
          </div>
        }
        key={index.toString()}
      >
        <div className="kuika-blocks-grid">
          {blocks.map((block) => (
            <BlockItem
              key={block.id}
              block={block}
              onUsedBlockStylesSet={(usedStyles: ICustomBlockUsedStyles, blockJson: string) => {
                return this.props.sd.initializeCustomBlockStyles(block.name, usedStyles, blockJson);
              }}
              onDelete={() => {
                this.setState({
                  itemToDelete: { id: block.id, name: block.name },
                  isDeleteModalVisible: true
                });
              }}
            />
          ))}
        </div>
      </Panel>
    );
  };

  renderBlocksContent = () => {
    const { searchText } = this.state;
    const { blocks, isBlocksLoading } = this.props.sd;

    // The catalogue in outline while it has nothing to show; a re-fetch keeps the blocks on screen.
    if (resolveLoadingPresentation(!!isBlocksLoading, blocks.length > 0) === "skeleton") {
      return (
        <KuikaSkeletonRegion label="Loading blocks" className="kuika-component-panel__skeleton">
          <KuikaSkeletonCardGrid count={4} />
        </KuikaSkeletonRegion>
      );
    }

    if (blocks.length === 0) {
      return (
        <KuikaEmptyState
          svgName="search-off"
          customName="No Blocks found"
          description="No blocks are available at the moment."
        />
      );
    }

    const filteredBlocks = (() => {
      this.ensureBlocksFzf(blocks);
      if (!searchText) return blocks;
      const q = this.handleCharsAndDeleteWhiteSpaces(searchText)?.toLowerCase() || "";
      return this.blocksFzf ? this.blocksFzf.find(q).map((r) => r.item) : blocks;
    })();

    if (searchText && filteredBlocks.length === 0) {
      return (
        <KuikaEmptyState
          svgName="search-off"
          customName="No Blocks found"
          description="We couldn't find any blocks matching your search."
        />
      );
    }

    // Group blocks by category
    const categorizedBlocks = filteredBlocks.reduce(
      (acc, block) => {
        const category = block.category && block.category.trim() !== "" ? block.category : "Uncategorized";
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(block);
        return acc;
      },
      {} as Record<string, BlockModel[]>
    );

    // Sort categories with "Custom" at the top and "Uncategorized" at the end
    const sortedCategories = Object.keys(categorizedBlocks).sort((a, b) => {
      if (a === "Custom") return -1;
      if (b === "Custom") return 1;
      if (a === "Uncategorized") return 1;
      if (b === "Uncategorized") return -1;
      return a.localeCompare(b);
    });

    return (
      <div className="kuika-blocks-container" data-testid="blocksPanelContent">
        <KuikaCollapsePanel defaultActiveKey={sortedCategories.map((_, index) => index.toString())}>
          {sortedCategories.map((category, index) =>
            this.renderBlockCategoryPanel(category, categorizedBlocks[category], index)
          )}
        </KuikaCollapsePanel>
      </div>
    );
  };

  // Widget Renderer component details from backend
  private static WIDGET_RENDERER_ID = "94d305b5-da35-4123-8839-36ad95fc29d7";

  private static WIDGET_RENDERER_NAME = "Widget Renderer";

  private static WIDGET_RENDERER_COMPONENT_TYPE = 3;

  private static WIDGET_RENDERER_BLOCK_STYLE = 2;

  handleWidgetDragStart = (event: any, project: WidgetProjectInfo, component: WidgetComponentInfo) => {
    const predefinedValues = {
      framework: project.framework?.toLowerCase() || "react",
      packId: project.projectId,
      componentId: component.widgetId
    };

    // Widget props for dynamic property/event creation
    const widgetProps = {
      resolvedProps: component.resolvedProps || []
    };

    const activeCanvasIFrame = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
    if (activeCanvasIFrame) {
      (activeCanvasIFrame.contentWindow as any).kuika.movingSourceElement = {
        id: ComponentToolKit.WIDGET_RENDERER_ID,
        name: ComponentToolKit.WIDGET_RENDERER_NAME,
        componentType: ComponentToolKit.WIDGET_RENDERER_COMPONENT_TYPE,
        blockStyle: ComponentToolKit.WIDGET_RENDERER_BLOCK_STYLE,
        predefinedValues,
        widgetProps
      } as IDraggableComponent;
    }
    this.props.sd.setSelectedDisplayItem(SpecialDIIds.rootDI.toString());
    event.dataTransfer.setData("name", ComponentToolKit.WIDGET_RENDERER_NAME);
    event.dataTransfer.setData("id", ComponentToolKit.WIDGET_RENDERER_ID);
    event.dataTransfer.setData("kuikaid", this.generateRandomId().toString());
    event.dataTransfer.setData("reason", EElementDragReason.CREATE);
    event.dataTransfer.setData(
      `kuikaComponentType=${ComponentToolKit.WIDGET_RENDERER_COMPONENT_TYPE}`,
      ComponentToolKit.WIDGET_RENDERER_COMPONENT_TYPE
    );
    event.dataTransfer.setData("predefinedValues", JSON.stringify(predefinedValues));
    event.dataTransfer.setData("widgetProps", JSON.stringify(widgetProps));

    const dragImage = document.createElement("div");
    dragImage.style.position = "absolute";
    dragImage.style.top = "3px";
    dragImage.style.left = "15px";
    dragImage.style.zIndex = "-1";

    const badge = (
      <div className="kuika-component-panel__drag-preview">
        <KuikaBadge
          className="kuika-padding-right-l"
          content={component.title?.toUpperCase()}
          leftIcon={<KuikaSvg name={"plus"} width={12} height={12} />}
          color="orange"
        />
        <div className="kuika-component-panel__drag-preview--copy-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g id="copy-plus" clipPath="url(#clip0_10513_34988)">
              <path
                id="Vector"
                d="M7 9.667C7 8.95967 7.28099 8.28131 7.78115 7.78115C8.28131 7.28099 8.95967 7 9.667 7H18.333C18.6832 7 19.03 7.06898 19.3536 7.20301C19.6772 7.33704 19.9712 7.53349 20.2189 7.78115C20.4665 8.0288 20.663 8.32281 20.797 8.64638C20.931 8.96996 21 9.31676 21 9.667V18.333C21 18.6832 20.931 19.03 20.797 19.3536C20.663 19.6772 20.4665 19.9712 20.2189 20.2189C19.9712 20.4665 19.6772 20.663 19.3536 20.797C19.03 20.931 18.6832 21 18.333 21H9.667C9.31676 21 8.96996 20.931 8.64638 20.797C8.32281 20.663 8.0288 20.4665 7.78115 20.2189C7.53349 19.9712 7.33704 19.6772 7.20301 19.3536C7.06898 19.03 7 18.6832 7 18.333V9.667Z"
                stroke="black"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                id="Vector_2"
                d="M4.012 16.737C3.705 16.5626 3.44965 16.31 3.2719 16.0049C3.09415 15.6998 3.00034 15.3531 3 15V5C3 3.9 3.9 3 5 3H15C15.75 3 16.158 3.385 16.5 4"
                stroke="black"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                id="Vector_3"
                d="M11 14H17"
                stroke="black"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                id="Vector_4"
                d="M14 11V17"
                stroke="black"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
            <defs>
              <clipPath id="clip0_10513_34988">
                <rect width="24" height="24" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </div>
      </div>
    );

    const buffer = document.getElementById("html-buffer");
    if (buffer) {
      ReactDOM.render(badge, dragImage);
      buffer.appendChild(dragImage);
      event.dataTransfer.setDragImage(dragImage, dragImage.clientWidth / 2, dragImage.clientHeight / 2);
      setTimeout(() => {
        try {
          buffer.removeChild(dragImage);
        } catch (e) {}
      }, 100);
    }
  };

  renderFrameworkIcon = (framework?: string): React.ReactNode => {
    const fw = framework?.toLowerCase();

    if (fw === "react") {
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" width={24} height={24}>
          <path d="M12 10.11c1.03 0 1.87.84 1.87 1.89 0 1-.84 1.85-1.87 1.85S10.13 13 10.13 12c0-1.05.84-1.89 1.87-1.89M7.37 20c.63.38 2.01-.2 3.6-1.7-.52-.59-1.03-1.23-1.51-1.9a22.7 22.7 0 0 1-2.4-.36c-.51 2.14-.32 3.61.31 3.96m.71-5.74-.29-.51c-.11.29-.22.58-.29.86.27.06.57.11.88.16l-.3-.51m6.54-.76.81-1.5-.81-1.5c-.3-.53-.62-1-.91-1.47C13.17 9 12.6 9 12 9s-1.17 0-1.71.03c-.29.47-.61.94-.91 1.47L8.57 12l.81 1.5c.3.53.62 1 .91 1.47.54.03 1.11.03 1.71.03s1.17 0 1.71-.03c.29-.47.61-.94.91-1.47M12 6.78c-.19.22-.39.45-.59.72h1.18c-.2-.27-.4-.5-.59-.72m0 10.44c.19-.22.39-.45.59-.72h-1.18c.2.27.4.5.59.72M16.62 4c-.62-.38-2 .2-3.59 1.7.52.59 1.03 1.23 1.51 1.9.82.08 1.63.2 2.4.36.51-2.14.32-3.61-.32-3.96m-.7 5.74.29.51c.11-.29.22-.58.29-.86-.27-.06-.57-.11-.88-.16l.3.51m1.45-7.05c1.47.84 1.63 3.05 1.01 5.63 2.54.75 4.37 1.99 4.37 3.68s-1.83 2.93-4.37 3.68c.62 2.58.46 4.79-1.01 5.63-1.46.84-3.45-.12-5.37-1.95-1.92 1.83-3.91 2.79-5.38 1.95-1.46-.84-1.62-3.05-1-5.63-2.54-.75-4.37-1.99-4.37-3.68s1.83-2.93 4.37-3.68c-.62-2.58-.46-4.79 1-5.63 1.47-.84 3.46.12 5.38 1.95 1.92-1.83 3.91-2.79 5.37-1.95M17.08 12c.34.75.64 1.5.89 2.26 2.1-.63 3.28-1.53 3.28-2.26s-1.18-1.63-3.28-2.26c-.25.76-.55 1.51-.89 2.26M6.92 12c-.34-.75-.64-1.5-.89-2.26-2.1.63-3.28 1.53-3.28 2.26s1.18 1.63 3.28 2.26c.25-.76.55-1.51.89-2.26m9 2.26-.3.51c.31-.05.61-.1.88-.16-.07-.28-.18-.57-.29-.86l-.29.51m-2.89 4.04c1.59 1.5 2.97 2.08 3.59 1.7.64-.35.83-1.82.32-3.96-.77.16-1.58.28-2.4.36-.48.67-.99 1.31-1.51 1.9M8.08 9.74l.3-.51c-.31.05-.61.1-.88.16.07.28.18.57.29.86l.29-.51m2.89-4.04C9.38 4.2 8 3.62 7.37 4c-.63.35-.82 1.82-.31 3.96a22.7 22.7 0 0 1 2.4-.36c.48-.67.99-1.31 1.51-1.9z" />
        </svg>
      );
    }

    if (fw === "vue") {
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" width={24} height={24}>
          <path d="M2 3h3.5L12 15l6.5-12H22L12 21 2 3m4.5 0h3L12 7.58 14.5 3h3L12 13.08 6.5 3z" />
        </svg>
      );
    }

    if (fw === "angular") {
      return (
        <svg viewBox="0 0 223 236" fill="currentColor" width={24} height={24}>
          <path d="m222.077 39.192-8.019 125.923L137.387 0l84.69 39.192Zm-53.105 162.825-57.933 33.056-57.934-33.056 11.783-28.556h92.301l11.783 28.556ZM111.039 62.675l30.357 73.803H80.681l30.358-73.803ZM7.937 165.115 0 39.192 84.69 0 7.937 165.115Z" />
        </svg>
      );
    }

    if (fw === "angularjs") {
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" width={24} height={24}>
          <path d="M12 2L2 7l1.5 12.5L12 22l8.5-2.5L22 7l-10-5m0 2.31l6.9 15.31H16l-1.31-3.5H9.35L8 19.62H5.09L12 4.31m0 4.65l-2.15 5.04h4.3L12 8.96z" />
        </svg>
      );
    }

    if (fw === "svelte") {
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" width={24} height={24}>
          <path d="M20.68 3.17a6.06 6.06 0 0 0-8.4-1.42l-5.1 3.4a5.63 5.63 0 0 0-2.54 3.79 5.82 5.82 0 0 0 .57 3.83 5.63 5.63 0 0 0-.85 2.11 5.91 5.91 0 0 0 1.02 4.47 6.06 6.06 0 0 0 8.4 1.42l5.1-3.4a5.63 5.63 0 0 0 2.54-3.79 5.82 5.82 0 0 0-.57-3.83 5.63 5.63 0 0 0 .85-2.11 5.91 5.91 0 0 0-1.02-4.47M9.34 21.04a3.63 3.63 0 0 1-3.9-1.6 3.54 3.54 0 0 1-.61-2.68 3.5 3.5 0 0 1 .13-.57l.13-.4.36.24a6.63 6.63 0 0 0 2.01 1.05l.19.05-.02.19a1.07 1.07 0 0 0 .19.7 1.09 1.09 0 0 0 1.17.48 1.13 1.13 0 0 0 .31-.12l5.1-3.4a1 1 0 0 0 .45-.68 1.06 1.06 0 0 0-.18-.8 1.09 1.09 0 0 0-1.17-.48 1.13 1.13 0 0 0-.31.12l-1.95 1.3a3.76 3.76 0 0 1-1.03.4 3.63 3.63 0 0 1-3.9-1.6 3.54 3.54 0 0 1-.61-2.68 3.34 3.34 0 0 1 1.52-2.27l5.1-3.4a3.76 3.76 0 0 1 1.03-.4 3.63 3.63 0 0 1 3.9 1.6 3.54 3.54 0 0 1 .61 2.68 3.5 3.5 0 0 1-.13.57l-.13.4-.36-.24a6.63 6.63 0 0 0-2.01-1.05l-.19-.05.02-.19a1.07 1.07 0 0 0-.19-.7 1.09 1.09 0 0 0-1.17-.48 1.13 1.13 0 0 0-.31.12l-5.1 3.4a1 1 0 0 0-.45.68 1.06 1.06 0 0 0 .18.8 1.09 1.09 0 0 0 1.17.48 1.13 1.13 0 0 0 .31-.12l1.95-1.3a3.76 3.76 0 0 1 1.03-.4 3.63 3.63 0 0 1 3.9 1.6 3.54 3.54 0 0 1 .61 2.68 3.34 3.34 0 0 1-1.52 2.27l-5.1 3.4a3.76 3.76 0 0 1-1.03.4z" />
        </svg>
      );
    }

    // Default: puzzle icon
    return <KuikaSvg name="puzzle" width={24} height={24} />;
  };

  handleDeleteCustomBlock = () => {
    if (!this.state.itemToDelete) return;

    this.props.sd.deleteBlock(this.state.itemToDelete.id);
    this.setState({ itemToDelete: undefined });
  };

  handleDeleteWidgetProject = async (project: WidgetProjectInfo) => {
    try {
      this.setState({ deletingWidgetPackId: project.projectId });
      const response = await WidgetsService.DeleteWidgetProject(project.projectId);

      if (response.data?.success) {
        // Remove from local state
        this.setState((prev) => ({
          widgetProjects: prev.widgetProjects.filter((p) => p.projectId !== project.projectId)
        }));
        KMainFunctions.displaySuccessNotification(`Widget pack "${project.name}" deleted successfully`);
      } else if (response.data?.usageCheck?.isInUse) {
        // Show usage error
        const usages = response.data.usageCheck.usages;
        const usageList = usages
          .slice(0, 5)
          .map((u) => `${u.projectName} / ${u.screenName}`)
          .join(", ");
        const moreCount = usages.length > 5 ? ` and ${usages.length - 5} more` : "";
        KMainFunctions.displayErrorNotification(
          `Cannot delete: Used in ${usages.length} screen(s): ${usageList}${moreCount}`
        );
      } else {
        KMainFunctions.displayErrorNotification(response.data?.message || "Failed to delete widget pack");
      }
    } catch (error: any) {
      KMainFunctions.displayErrorNotification(error?.message || "Failed to delete widget pack");
    } finally {
      this.setState({ deletingWidgetPackId: null });
    }
  };

  renderWidgetProjectPanel = (project: WidgetProjectInfo, index: number) => {
    const isDeleting = this.state.deletingWidgetPackId === project.projectId;

    return (
      <Panel
        // The whole header row is the hover host, so the pack name slides while the row's
        // hover-only delete button is showing too.
        headerClass={OVERFLOW_REVEAL_HOST_CLASS}
        header={
          <div
            className="kuika-widget-project-header"
            id={`widgets-panel-header-${this.handleCharsAndDeleteWhiteSpaces(project.name)?.toLowerCase()}`}
          >
            <span className="kuika-widget-project-header__name" title={project.name}>
              <KuikaOverflowText>{project.name}</KuikaOverflowText>
            </span>
          </div>
        }
        extra={
          <div onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="kuika-widget-delete-btn"
              onClick={() => this.setState({ deleteConfirmProject: project })}
              disabled={isDeleting}
              aria-label="Delete widget pack"
              title="Delete widget pack"
            >
              {isDeleting ? <KuikaSmallSpinner /> : <KuikaSvg name="trash" width={14} height={14} />}
            </button>
          </div>
        }
        key={index.toString()}
      >
        <div className="kuika-widgets-grid">
          {project.components.map((component) => (
            <div
              key={component.widgetId}
              className="kuika-widget-item"
              draggable={true}
              onDragStart={(e) => this.handleWidgetDragStart(e, project, component)}
              onDragEnter={this.handleDragEnter}
            >
              <div className="kuika-widget-item__icon">{this.renderFrameworkIcon(project.framework)}</div>
              <div className="kuika-widget-item__title">{component.title}</div>
            </div>
          ))}
        </div>
      </Panel>
    );
  };

  getTabOptions = (): IComponentPanelTabOption[] => {
    const baseOptions: IComponentPanelTabOption[] = [
      { label: "Elements", value: "elements", dataTestId: "componentPanelElementsTab" },
      { label: "Blocks", value: "blocks", dataTestId: "componentPanelBlocksTab" }
    ];
    const isWebProject = this.props.project.projectType === EProjectType.Desktop;
    const isWebScreen = this.props.sd.getIsWebScreen();
    const shouldShowWidgets = isWebProject || isWebScreen;
    if (shouldShowWidgets) {
      baseOptions.push({ label: "Widgets", value: "widgets", dataTestId: "componentPanelWidgetsTab" });
    }
    return baseOptions;
  };

  renderWidgetsContent = () => {
    const { widgetProjects, isWidgetsLoading, searchText } = this.state;

    // The widget packs in outline while there are none to show; a re-fetch keeps them on screen.
    if (resolveLoadingPresentation(isWidgetsLoading, widgetProjects.length > 0) === "skeleton") {
      return (
        <KuikaSkeletonRegion label="Loading widgets" className="kuika-component-panel__skeleton">
          <KuikaSkeletonList rows={5} withIcon />
        </KuikaSkeletonRegion>
      );
    }

    if (widgetProjects.length === 0) {
      return (
        <KuikaEmptyState
          svgName="puzzle"
          customName="No Widgets found"
          description={
            <>
              No widget projects are available at the moment.{" "}
              <a href="https://academy.kuika.com/en/content/widgets" target="_blank" rel="noreferrer">
                Learn more
              </a>
            </>
          }
        />
      );
    }

    const filteredProjects = (() => {
      if (!searchText) return widgetProjects;
      const q = this.handleCharsAndDeleteWhiteSpaces(searchText)?.toLowerCase() || "";
      return widgetProjects.filter(
        (project) =>
          project.name.toLowerCase().includes(q) ||
          project.projectId.toLowerCase().includes(q) ||
          project.components.some((c) => c.title.toLowerCase().includes(q) || c.componentName.toLowerCase().includes(q))
      );
    })();

    if (searchText && filteredProjects.length === 0) {
      return (
        <KuikaEmptyState
          svgName="search-off"
          customName="No Widgets found"
          description="We couldn't find any widgets matching your search."
        />
      );
    }

    return (
      <div className="kuika-widgets-container">
        <KuikaCollapsePanel defaultActiveKey={filteredProjects.map((_, index) => index.toString())}>
          {filteredProjects.map((project, index) => this.renderWidgetProjectPanel(project, index))}
        </KuikaCollapsePanel>
      </div>
    );
  };

  /**
   * The scroll area's one body. Extracted because the four-way choice (placeholder / no
   * search results / the catalogue / blocks / widgets) was a nested ternary chain, which
   * CLAUDE.md bans and which the first-load placeholder would only have deepened.
   */
  renderPanelContent = (args: IPanelContentArgs): JSX.Element => {
    if (args.showCatalogueSkeleton) {
      return <ComponentToolKitSkeleton />;
    }
    if (args.mode === "blocks") {
      return this.renderBlocksContent();
    }
    if (args.mode !== "elements") {
      return this.renderWidgetsContent();
    }
    if (args.searchText && args.searchText.length > 1 && !args.hasSearchResults) {
      return (
        <KuikaEmptyState
          svgName="search-off"
          customName="No Element found"
          description="We couldn't find any items matching your search."
        />
      );
    }
    const isSearching: boolean = args.searchText.length > 0;
    return (
      <KuikaCollapsePanel
        defaultActiveKey={["0", "1", "2", "99"]}
        activeKey={isSearching ? ["0", "1", "2", "3", "4", "5", "6", "7", "8"] : args.activeKeys}
        onChange={this.onPanelChange}
      >
        {isSearching ? <></> : this.props.designType !== "report" && args.commonlyUsedComponents}
        {this.getFilteredCategories().map((category, index) => this.renderCategoryPanel(category, index))}
      </KuikaCollapsePanel>
    );
  };

  render() {
    const { searchText, activeKeys, mode } = this.state;
    const commonlyUsedComponents = this.getCommonlyUsedComponents();
    const categoryList = this.getFilteredCategories();
    // See first-load-skeletons for why this is not simply `!isProjectLoaded`.
    const showCatalogueSkeleton: boolean = shouldShowCatalogueSkeleton(this.props.sd);

    const hasSearchResults = categoryList.some((category) => {
      const filteredComponents = this.getFilteredComponents(category)
        .filter(({ name }) => {
          const normalizedSearchText = this.handleCharsAndDeleteWhiteSpaces(searchText)?.toLowerCase();
          const isKeywordFound = this.state.foundedKeywords.some(
            (keyword) => keyword.toLowerCase() === name.toLowerCase()
          );

          if (isKeywordFound) return true;
          if (!searchText || searchText === "" || searchText.length <= 1) return true;
          return name.toLowerCase().includes(normalizedSearchText);
        })
        .filter(({ categoryName }) => categoryName === category.categoryName);

      return filteredComponents.length > 0;
    });

    return (
      <>
        <div className="kuika-left-panel__container kuika-component-panel">
          {this.props.sd.dashboardState !== DashboardState.reportDesigner && (
            <div className="kuika-component-panel__content_switcher_container">
              <KuikaTabs
                dataTestId="componentPanelModeTabs"
                value={mode}
                options={this.getTabOptions()}
                onChange={this.onModeChange}
              />
            </div>
          )}
          <SearchablePanelHeader
            searchText={searchText}
            onSearchTextChange={this.onSearchTextChange}
            onSearchClearCallback={this.deleteSearchText}
            viewPart={
              this.props.sd.dashboardState === DashboardState.reportDesigner
                ? ViewPart.ReportDesignerPropertiesPanel
                : ViewPart.ComponentsPanel
            }
            panelName={
              this.state.mode === "elements" ? "Elements" : this.state.mode === "blocks" ? "Blocks" : "Widgets"
            }
            isSearchActive={this.state.isSearchActive}
            setSearchActive={this.setSearchActive}
          />
          <div
            className="kuika-left-panel__content"
            style={{
              // Measured offsets; the old 114px / 160px also reserved the removed 24px footer.
              height:
                this.props.sd.dashboardState === DashboardState.reportDesigner
                  ? // No tab switcher here, so the search header is the stretched top strip.
                    "calc(100vh - 85px - var(--panel-strip-growth, 0px))"
                  : // 49px top bar + 46px switcher + 36px search header.
                    "calc(100vh - 131px)"
            }}
          >
            {/* Elements, Blocks and Widgets swap under their tabs; the pane for a mode is drawn from
                that mode, so the leaving one is still the list it was. */}
            <KuikaTabContent
              activeKey={mode}
              order={this.getTabOptions().map((option: IComponentPanelTabOption): string => option.value)}
              render={(paneMode: string): JSX.Element =>
                this.renderPanelContent({
                  showCatalogueSkeleton,
                  mode: paneMode,
                  searchText,
                  activeKeys,
                  hasSearchResults,
                  commonlyUsedComponents
                })
              }
            />
          </div>
        </div>
        <DeleteModal
          itemName={this.state.itemToDelete?.name ?? ""}
          triggerModal={this.state.isDeleteModalVisible ?? false}
          deleteItem={() => this.handleDeleteCustomBlock()}
          deleteType={"custom block"}
          closeModal={() => this.setState({ isDeleteModalVisible: false })}
        />
        <KModal
          showModal={!!this.state.deleteConfirmProject}
          closeModal={() => this.setState({ deleteConfirmProject: null })}
          header="Delete widget pack"
          width={400}
          centered
          footer={
            <KModalFooter
              onCancel={() => this.setState({ deleteConfirmProject: null })}
              submitLabel="Delete"
              submitTone="danger"
              onSubmit={() => {
                if (this.state.deleteConfirmProject) {
                  this.handleDeleteWidgetProject(this.state.deleteConfirmProject);
                }
                this.setState({ deleteConfirmProject: null });
              }}
            />
          }
        >
          <div className="kuika-widget-pack-delete-confirm">
            <KuikaModalIconWrapper iconName="trash" intent="destructive" />
            <KuikaH5 className="kuika-widget-pack-delete-confirm__title">Delete widget pack</KuikaH5>
            <KuikaText className="kuika-widget-pack-delete-confirm__text">
              Are you sure you want to delete &quot;{this.state.deleteConfirmProject?.name}&quot;? This action cannot be
              undone.
            </KuikaText>
          </div>
        </KModal>
      </>
    );
  }
}

export default ComponentToolKit;
